import memoize from 'memoize-one';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import sortBy from 'sort-by';

import { useDialogStateManager, useStandardApiRequest } from 'my-core/hooks';
import { useOnlineCourseContext } from 'my-core/in-course-experience-hooks';

import OnlineCoursePurchaseForm from 'my-components/OnlineCoursePurchaseForm';

import StudentCoursePagePracticeMode from './StudentCoursePagePracticeMode';

import { toggleLoginForm } from 'my-actions/AppStateActions';
import { fetchOnlineCoursePracticeModeSubmissions } from 'my-actions/OnlineCourseActions';
import { createOnlineCourseUser } from 'my-actions/OnlineCourseUserActions';
import { createOCPracticeQuestionTokenPurchase } from 'my-actions/TokenPurchaseActions';

export default function StudentCoursePagePracticeModeCtnr({
  activeComponent,
  classPassId,
  course,
  currentUser,
  drawerOpen,
  fetchStatus,
  onToggleDrawer,
  pathMatchData,
}) {
  // reselecting onlineCourse from store because on passed down depends on state.onlineCourseUsers which
  // changes often, causing memoized values to update (eg. eventTrackingProperties)
  const classPass = useSelector(state => state.onlineCourses.items[classPassId]);
  const { courseSubscription, onlineCourseUser, subscription, userCourse } = useOnlineCourseContext(
    currentUser,
    classPass,
    course,
  );
  // ONLINE COURSE TOKEN PURCHASES
  const tokenPurchases = useSelector(state =>
    getTokenPurchases(classPassId, currentUser.id, state.tokenPurchases.items),
  );
  const { performRequest: performTokenPurchase, requestStatus: tokenPurchaseStatus } = useStandardApiRequest({
    actionCreator: createOCPracticeQuestionTokenPurchase,
    errorMessage: true,
    successMessage: 'Free activity redeemed',
  });
  const purchaseWithToken = useCallback(
    (qqId, sectionId) => {
      performTokenPurchase(classPassId, qqId, sectionId);
    },
    [performTokenPurchase, classPassId],
  );

  // CREATE ONLINE COURSE USER
  const { performRequest: performCreateOCU /*, requestStatus: createOCUStatus */ } = useStandardApiRequest({
    actionCreator: createOnlineCourseUser,
  });
  useEffect(() => {
    if (!fetchStatus?.success || currentUser.guest || onlineCourseUser?.id) return;
    performCreateOCU(classPassId);
  }, [currentUser.guest, fetchStatus?.success, classPassId, onlineCourseUser?.id, performCreateOCU]);

  // PRACTICE MODE SUBMISSIONS
  const fetchSectionSubmissionsKey = `fetch_online_course_practice_mode_section_submissions_${activeComponent?.data?.id}`;
  const { performRequest: performFetchSectionSubmissions, requestStatus: fetchSectionSubmissionsStatus } =
    useStandardApiRequest({
      actionCreator: fetchOnlineCoursePracticeModeSubmissions,
      errorMessage: true,
      requestKey: fetchSectionSubmissionsKey,
    });

  const isSection = activeComponent?.type === 'section';
  useEffect(() => {
    if (!fetchSectionSubmissionsStatus && isSection) {
      performFetchSectionSubmissions(classPassId, activeComponent.data.id, fetchSectionSubmissionsKey);
    }
  }, [
    activeComponent?.data?.id,
    fetchSectionSubmissionsStatus,
    fetchSectionSubmissionsKey,
    isSection,
    classPassId,
    performFetchSectionSubmissions,
  ]);
  const sectionSubmissions = useSelector(state =>
    getSectionSubmissions(activeComponent?.data?.id, state.quizQuestionSubmissions.items, state.quizQuestions.items),
  );

  // ONLINE COURSE PURCHASE
  const dispatch = useDispatch();
  const [purchaseDialogOpen, hasPurchaseDialogOpened, purchaseDialogActions] = useDialogStateManager();
  const showPurchaseForm = useCallback(() => {
    if (currentUser.guest) {
      dispatch(
        toggleLoginForm({
          actionText: 'See Practice Solutions',
          source: 'in_course_purchase',
        }),
      );
    } else {
      purchaseDialogActions.open(true);
    }
  }, [currentUser.guest, dispatch, purchaseDialogActions]);

  return (
    <>
      <StudentCoursePagePracticeMode
        activeComponent={activeComponent}
        basePath={`../${pathMatchData.pathname}`}
        classPass={classPass}
        courseSubscription={courseSubscription}
        currentUser={currentUser}
        drawerOpen={drawerOpen}
        fetchingSubmissions={!fetchSectionSubmissionsStatus || fetchSectionSubmissionsStatus.fetching}
        onlineCourseUser={onlineCourseUser}
        onPurchase={showPurchaseForm}
        onToggleDrawer={onToggleDrawer}
        onTokenPurchase={purchaseWithToken}
        sectionSubmissions={sectionSubmissions}
        subscription={subscription}
        tokenPurchases={tokenPurchases}
        tokenPurchaseStatus={tokenPurchaseStatus}
        userCourse={userCourse}
      />
      {hasPurchaseDialogOpened && (
        <OnlineCoursePurchaseForm
          onClose={purchaseDialogActions.close}
          onlineCourseId={classPassId}
          open={purchaseDialogOpen}
        />
      )}
    </>
  );
}

const getSectionSubmissions = memoize((sectionId, submissions, quizQuestions) => {
  if (sectionId) {
    return Object.values(submissions)
      .filter(s => s.practice_mode_section_id === sectionId)
      .map(s => ({ ...s, quiz_question: quizQuestions[s.quiz_question_id] }))
      .sort(sortBy('practice_mode_order'));
  }
});

const getTokenPurchases = memoize((ocId, userId, tokenPurchases) =>
  Object.values(tokenPurchases).filter(tp => tp.user_id === userId && tp.online_course_id === ocId),
);
